import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Waves, Sparkles, Users, Briefcase, Snowflake, Accessibility } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { CtaBlock } from "../components/CtaBlock";
import { FaqAccordion, buildFaqLd } from "../components/FaqAccordion";
import { buildBreadcrumbLd } from "../components/Breadcrumbs";
import { Picture, findPhoto } from "../lib/photos";
import { useSeo } from "../lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POOL_FAQS = [
  { q: "Is the indoor pool heated?", a: "Yes — heated year-round when it's open. The pool sits on the ground floor of the hotel." },
  { q: "When is the pool closed for maintenance?", a: "Typically November through mid-January. Please call the front desk at (641) 437-7272 before your stay to confirm current pool status." },
  { q: "Is there an ADA pool lift?", a: "Yes. An accessible ADA lift is installed at the pool so guests using mobility devices can transfer in and out safely." },
  { q: "Do I need to bring my own towel?", a: "Fresh pool towels are provided at the pool. Just leave them on the return cart when you're done." },
  { q: "Can kids swim without a parent?", a: "Children must be supervised at all times. There is no lifeguard on duty — supervision is the parent's responsibility." },
  { q: "Is the pool free for hotel guests?", a: "Yes. The pool is included with every stay at no additional charge." },
  { q: "Is the pool open to the public?", a: "The pool is reserved for registered hotel guests." },
  { q: "What time does the pool open?", a: "Pool hours vary by season. Check the sign at the pool entrance or the lobby for current hours." },
];

export default function IndoorPool() {
  const [photos, setPhotos] = useState([]);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/photos`).then((r) => {
      if (alive) setPhotos(r.data.photos || []);
    });
    return () => { alive = false; };
  }, []);

  const hero = useMemo(
    () => findPhoto(photos, "westbridge-inn-suites-centerville-iowa-indoor-pool-wide"),
    [photos],
  );
  const secondary = useMemo(
    () => findPhoto(photos, "westbridge-inn-suites-centerville-iowa-indoor-swimming-pool"),
    [photos],
  );

  const canonical = "https://westbridgehotels.com/indoor-pool";
  useSeo({
    title: "Hotel with Indoor Pool in Centerville, Iowa — Westbridge Inn & Suites",
    description:
      "The heated indoor pool at Westbridge Inn & Suites — the only year-round hotel pool a short drive from Rathbun Lake. ADA lift, family friendly, free with every stay.",
    canonical,
    image: hero ? `${window.location.origin}${hero.jpg}` : undefined,
    jsonLd: [
      buildBreadcrumbLd([{ label: "Indoor Pool", to: "/indoor-pool" }]),
      buildFaqLd(POOL_FAQS),
    ],
  });

  return (
    <main id="main" data-testid="indoor-pool-page">
      <PageHero
        eyebrow="Indoor Pool"
        title="A heated indoor pool in southern Iowa — year-round."
        intro="Rain or Iowa snow, our indoor pool is warm, ground-floor, and free with every stay. It's one of the biggest reasons families and Rathbun Lake weekenders book the room next door."
        photo={hero}
        breadcrumbs={[{ label: "Indoor Pool", to: "/indoor-pool" }]}
        testId="indoor-pool-hero"
      />

      {/* Value story */}
      <section className="bg-cream py-16 md:py-24">
        <div className="container-app grid md:grid-cols-12 gap-10 md:gap-14 items-center">
          <div className="md:col-span-6">
            <div className="block w-full aspect-[4/5] overflow-hidden bg-forest-800">
              {secondary && (
                <Picture
                  photo={secondary}
                  className="block w-full h-full"
                  imgClassName="w-full h-full object-cover"
                  sizes="(min-width: 768px) 45vw, 100vw"
                />
              )}
            </div>
          </div>
          <div className="md:col-span-6 max-w-xl">
            <p className="eyebrow mb-4">Why guests book us</p>
            <h2 className="font-display font-light text-3xl md:text-5xl text-forest-900 tracking-tight leading-[1.05]">
              A rainy-day <span className="italic text-brass">swimming holiday</span>, on the road to Rathbun.
            </h2>
            <p className="mt-6 text-ink/85 text-lg leading-relaxed">
              Centerville sits in Iowa lake country. Most nearby lodging is seasonal or dry. Ours is neither: the indoor pool is heated most of the year and lives on the ground floor with an accessible ADA lift, so guests of every ability can use it.
            </p>
            <p className="mt-4 text-ink/85 text-lg leading-relaxed">
              For families, it's the make-or-break amenity. For business travelers, it's the twenty minutes that undo a long day.
            </p>
          </div>
        </div>
      </section>

      {/* Three audiences */}
      <section className="bg-cream-100 py-16 md:py-24 paper-grain">
        <div className="container-app relative">
          <div className="max-w-2xl mb-12">
            <p className="eyebrow mb-3">Who books us for the pool</p>
            <h2 className="font-display font-light text-3xl md:text-5xl text-forest-900 tracking-tight leading-tight">
              Three honest reasons.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <AudienceCard
              icon={Users}
              title="Families near Rathbun"
              body="Rathbun Lake and Honey Creek are seasonal outdoor destinations. On a rainy afternoon — or after a long day at the beach — the indoor pool is the reason the kids stop asking to go home. Interior-corridor rooms are steps away."
              cta={{ label: "See our family-friendly rooms", to: "/rooms/double-queen" }}
            />
            <AudienceCard
              icon={Briefcase}
              title="Business travelers"
              body="Twenty minutes in a warm pool after a day on the road is the fastest recovery Iowa offers. Interior-corridor Kings are best — quieter halls, quick walk to the pool, back to the room without stepping outside."
              cta={{ label: "See our King room", to: "/rooms/king" }}
            />
            <AudienceCard
              icon={Snowflake}
              title="Winter travelers"
              body="When outdoor pools are drained and the countryside is under snow, our heated pool is one of the few in southern Iowa still open. Check with the front desk on winter status — we maintain the pool typically Nov–mid Jan."
              cta={{ label: "Read our winter FAQ →", href: "#pool-faq" }}
            />
          </div>
        </div>
      </section>

      {/* ADA + maintenance callouts */}
      <section className="bg-forest-900 text-cream py-16 md:py-24">
        <div className="container-app grid md:grid-cols-2 gap-10 md:gap-14">
          <div className="p-8 md:p-10 bg-forest-800/60 border border-forest-700/70">
            <Accessibility size={22} className="text-brass-300 mb-4" strokeWidth={1.5} />
            <h3 className="font-display text-2xl md:text-3xl mb-3 text-cream">
              ADA access lift on the pool.
            </h3>
            <p className="text-cream/85 leading-relaxed">
              An accessible ADA lift is installed at the pool so guests using mobility devices can transfer safely into and out of the water. Accessible King rooms sit on a step-free route to the pool.
            </p>
            <Link to="/amenities/accessible-rooms" className="mt-6 inline-flex items-center gap-2 brass-underline text-brass-300">
              Read our accessibility features →
            </Link>
          </div>
          <div className="p-8 md:p-10 bg-forest-800/60 border border-forest-700/70">
            <Sparkles size={22} className="text-brass-300 mb-4" strokeWidth={1.5} />
            <h3 className="font-display text-2xl md:text-3xl mb-3 text-cream">
              Honest about maintenance.
            </h3>
            <p className="text-cream/85 leading-relaxed">
              The pool undergoes seasonal maintenance typically from November through mid-January. If the pool is why you're booking, please call the front desk at (641) 437-7272 to confirm current status before you drive out.
            </p>
            <p className="mt-4 text-cream/60 text-sm">
              We'd rather earn your second stay than upsell the first.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="pool-faq" className="bg-cream py-16 md:py-24" data-testid="indoor-pool-faq">
        <div className="container-app grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <p className="eyebrow mb-3">Common questions</p>
            <h2 className="font-display font-light text-3xl md:text-5xl text-forest-900 tracking-tight leading-[1.05]">
              About the pool.
            </h2>
          </div>
          <div className="md:col-span-8">
            <FaqAccordion items={POOL_FAQS} testIdPrefix="indoor-pool-faq" />
          </div>
        </div>
      </section>

      <CtaBlock
        eyebrow="Book with the pool in mind"
        title="Interior rooms sit steps from the water."
        body="Our 22 interior-corridor rooms — Double Queen, King, King Jacuzzi Suite, Accessible King — are the shortest walk to the pool and breakfast."
      />
    </main>
  );
}

function AudienceCard({ icon: Icon, title, body, cta }) {
  return (
    <article className="bg-cream border border-tan p-7 flex flex-col">
      <Icon size={24} className="text-brass mb-4" strokeWidth={1.5} />
      <h3 className="font-display text-xl md:text-2xl text-forest-900 mb-3 tracking-tight">
        {title}
      </h3>
      <p className="text-ink/80 leading-relaxed text-[0.98rem] grow">{body}</p>
      {cta?.to && (
        <Link
          to={cta.to}
          className="mt-5 inline-flex items-center gap-1 text-sm text-forest-900 brass-underline"
        >
          {cta.label}
        </Link>
      )}
      {cta?.href && (
        <a
          href={cta.href}
          className="mt-5 inline-flex items-center gap-1 text-sm text-forest-900 brass-underline"
        >
          {cta.label}
        </a>
      )}
    </article>
  );
}
